import { BatchUploadStats, FileUploadItem, UploadStatus } from './types';

export const createFileUploadItemId = (): string => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }

    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

export const createFileUploadItems = (files: File[]): FileUploadItem[] =>
    files.map(file => ({
        id: createFileUploadItemId(),
        file,
        status: 'pending',
    }));

export const computeUploadStats = (items: FileUploadItem[]): BatchUploadStats => ({
    total: items.length,
    success: items.filter(item => item.status === 'success').length,
    failed: items.filter(item => item.status === 'failed').length,
    pending: items.filter(item => item.status === 'pending').length,
    uploading: items.filter(item => item.status === 'uploading').length,
    cancelled: items.filter(item => item.status === 'cancelled').length,
});

export const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) {
        return `${bytes} B`;
    }

    if (bytes < 1024 * 1024) {
        return `${(bytes / 1024).toFixed(1)} KB`;
    }

    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const getUploadStatusLabel = (status: UploadStatus): string => {
    switch (status) {
        case 'uploading':
            return 'Uploading';
        case 'success':
            return 'Success';
        case 'failed':
            return 'Failed';
        case 'cancelled':
            return 'Cancelled';
        default:
            return 'Pending';
    }
};

export const getRateLimitSecondsRemaining = (resumeAt?: number, now = Date.now()): number | undefined => {
    if (resumeAt === undefined) {
        return undefined;
    }

    return Math.max(0, Math.ceil((resumeAt - now) / 1000));
};

export type RateLimitReason = 'proactive' | 'failure_retry';

export const formatRateLimitWaitRemaining = (seconds?: number): string | undefined => {
    if (seconds === undefined) {
        return undefined;
    }

    if (seconds >= 60) {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;

        if (remainingSeconds > 0) {
            return `${minutes} min ${remainingSeconds} sec`;
        }

        return `${minutes} min`;
    }

    return `${seconds} sec`;
};

export const getUploadProgressSummary = (
    stats: BatchUploadStats,
    isUploading: boolean,
    isPaused = false,
    options?: {
        isRateLimited?: boolean;
        rateLimitSecondsRemaining?: number;
        rateLimitReason?: RateLimitReason;
    },
): { title: string; subtitle?: string } => {
    const completed = stats.success + stats.failed + stats.cancelled;
    const isRateLimited = options?.isRateLimited ?? false;
    const rateLimitSecondsRemaining = options?.rateLimitSecondsRemaining;
    const rateLimitReason = options?.rateLimitReason;
    const formattedWait = formatRateLimitWaitRemaining(rateLimitSecondsRemaining);

    if (isPaused && stats.uploading === 0 && stats.pending > 0) {
        return {
            title: `Paused: ${completed} of ${stats.total} uploaded`,
        };
    }

    if (isRateLimited && (stats.pending > 0 || stats.uploading > 0)) {
        const waitDetail = formattedWait
            ? ` Please wait ${formattedWait} before uploads resume.`
            : ' Please wait before uploads resume.';

        if (rateLimitReason === 'failure_retry') {
            return {
                title: `Upload paused (${completed} of ${stats.total})`,
                subtitle: `Retrying upload shortly.${waitDetail}`,
            };
        }

        return {
            title: `Upload paused — request limit reached (${completed} of ${stats.total})`,
            subtitle: `The upload rate limit was reached to protect the server.${waitDetail} Uploads will resume automatically.`,
        };
    }

    if (isUploading) {
        return {
            title: `Uploading documents (${completed} of ${stats.total})`,
            subtitle: stats.uploading > 0 ? `${stats.uploading} currently uploading` : undefined,
        };
    }

    if (stats.failed > 0 || stats.cancelled > 0) {
        const parts = [`${stats.success} succeeded`];
        if (stats.failed > 0) {
            parts.push(`${stats.failed} failed`);
        }
        if (stats.cancelled > 0) {
            parts.push(`${stats.cancelled} cancelled`);
        }

        return {
            title: 'Upload complete',
            subtitle: parts.join(', '),
        };
    }

    return {
        title: 'Upload complete',
        subtitle: `${stats.success} document${stats.success === 1 ? '' : 's'} uploaded successfully`,
    };
};

export const getUploadErrorMessage = (error: unknown): string => {
    if (isUploadAborted(error)) {
        return 'Upload cancelled';
    }

    if (error instanceof Response) {
        return `${error.status} ${error.statusText || 'Request failed'}`;
    }

    if (error instanceof Error && error.message) {
        return error.message;
    }

    if (typeof error === 'string' && error) {
        return error;
    }

    return 'Upload failed';
};

export const isUploadAborted = (error: unknown): boolean => {
    if (error instanceof DOMException && error.name === 'AbortError') {
        return true;
    }

    if (error instanceof Error && error.name === 'AbortError') {
        return true;
    }

    return false;
};

export const createUploadAbortError = (message = 'Upload cancelled'): DOMException =>
    new DOMException(message, 'AbortError');

const fileMatchesAcceptToken = (file: File, token: string): boolean => {
    if (token === '*/*') {
        return true;
    }

    if (token.endsWith('/*')) {
        const typePrefix = token.slice(0, token.indexOf('/'));
        return file.type.startsWith(`${typePrefix}/`);
    }

    if (token.startsWith('.')) {
        return file.name.toLowerCase().endsWith(token.toLowerCase());
    }

    return file.type === token;
};

export const filterFilesByAccept = (files: File[], accept: string): File[] => {
    const tokens = accept
        .split(',')
        .map(token => token.trim())
        .filter(Boolean);

    if (tokens.length === 0) {
        return files;
    }

    return files.filter(file => tokens.some(token => fileMatchesAcceptToken(file, token)));
};
